import { queryOptions } from '@tanstack/react-query';
import axios from 'axios';

import type { PdfDocumentType, ServerResponse } from '../../@types/types';
import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';

export const getPdfDocument = async (
  id: string,
  endorsementId: string,
  resourceName: 'templates' | 'submissions' | 'endorsements' | 'brandings',
  pdfDocumentType: PdfDocumentType,
  expectedVersion?: number,
): Promise<ServerResponse<{ url: string; documentVersion: number }>> => {
  let url = `${resourceName}/${id}/pdf/download`;

  if (resourceName === 'endorsements') {
    url = `submissions/${id}/${resourceName}/${endorsementId}/pdf/download`;
  }

  if (resourceName === 'brandings') {
    url = `brandings/${id}/preview`;
  }

  url = `${url}?type=${pdfDocumentType}`;

  try {
    const networkPayload = await getRequestAuthPayload({
      method: 'get',
      url,
    });
    const response = await axios(networkPayload);

    const serverResponse: ServerResponse<{ url: string; documentVersion: number }> = response.data;
    if (expectedVersion && serverResponse?.data?.documentVersion < expectedVersion) {
      return Promise.reject('Pdf version does not match');
    }

    return serverResponse;
  } catch (err: any) {
    if (err?.response) {
      return Promise.reject(
        `Pdf download request failed with status: ${err.response?.status} and message: ${JSON.stringify(
          err.response?.data,
        )}`,
      );
    } else {
      return Promise.reject(`Pdf download request failed with message: ${err.message}`);
    }
  }
};

interface PdfDocumentQueryParams {
  id: string;
  endorsementId: string;
  resourceName: 'templates' | 'submissions' | 'endorsements' | 'brandings';
  pdfDocumentType: PdfDocumentType;
  expectedVersion?: number;
}

export const pdfDocumentQueries = {
  forDocument: ({ id, endorsementId, resourceName, pdfDocumentType, expectedVersion }: PdfDocumentQueryParams) =>
    queryOptions({
      queryKey: ['documents', id, endorsementId, resourceName, pdfDocumentType, expectedVersion] as const,
      queryFn: () => getPdfDocument(id, endorsementId, resourceName, pdfDocumentType, expectedVersion),
    }),
} satisfies QueryFactory;
